import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { BRAND } from "../../lib/brand.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { InquiryForm } from "../../components/forms/InquiryForm.jsx";
import { SEO } from "../../lib/seo.jsx";

const SPECIALISED = [
  {
    to: "/contact-us/new-build",
    title: "New build enquiry",
    body: "I'm planning a new home and want to chat through designs, blocks, and timelines.",
  },
  {
    to: "/contact-us/preconstruction",
    title: "Pre-construction",
    body: "I've signed and I'm in the planning, permits, or selections stage.",
  },
  {
    to: "/contact-us/maintenance",
    title: "Maintenance",
    body: "Existing homeowner — log a maintenance request or warranty claim.",
  },
  {
    to: "/request-an-appointment",
    title: "Book an appointment",
    body: "Drop in, or arrange a time that suits you — at our display centre or via video.",
  },
];

export default function ContactPage() {
  const brand = BRAND;

  return (
    <>
      <SEO
        title="Contact Us"
        description="Reach Shere-e-Punjab Homes — phone, email, address, or send us an enquiry online."
      />

      <PageHero
        eyebrow="Contact"
        title="Get in touch"
        description="We reply to every enquiry within one business day. For specific topics, the dedicated forms below route to the right person faster."
      />

      <Container className="py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_440px] lg:gap-16">
          <div>
            <section className="rounded-2xl border border-border-subtle bg-bg-elevated p-8">
              <h2 className="font-display text-2xl text-text-primary">Studio details</h2>
              <ul className="mt-6 space-y-4 text-sm">
                {brand.address && (
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span className="text-text-secondary">
                      {brand.address}
                      {brand.serviceAreaLine && (
                        <span className="mt-1 block font-medium text-accent">
                          {brand.serviceAreaLine}
                        </span>
                      )}
                    </span>
                  </li>
                )}
                {brand.phone && (
                  <li className="flex items-center gap-3">
                    <Phone className="h-4 w-4 shrink-0 text-accent" />
                    <a
                      href={`tel:${brand.phone.replace(/\s+/g, "")}`}
                      className="text-text-secondary hover:text-accent"
                    >
                      {brand.phone}
                    </a>
                  </li>
                )}
                {brand.email && (
                  <li className="flex items-center gap-3">
                    <Mail className="h-4 w-4 shrink-0 text-accent" />
                    <a
                      href={`mailto:${brand.email}`}
                      className="text-text-secondary hover:text-accent"
                    >
                      {brand.email}
                    </a>
                  </li>
                )}
                <li className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span className="text-text-secondary">
                    Mon–Fri 9am – 5pm · Sat by appointment · Closed Sun
                  </span>
                </li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="font-display text-2xl text-text-primary">Faster routes</h2>
              <p className="mt-2 text-sm text-text-secondary">
                Pick the form that matches your enquiry — it goes straight to the right team.
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {SPECIALISED.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className="group block rounded-2xl border border-border-subtle bg-bg-elevated p-5 transition-colors hover:border-accent"
                  >
                    <p className="font-display text-lg text-text-primary group-hover:text-accent">
                      {s.title}
                    </p>
                    <p className="mt-1 text-sm text-text-secondary">{s.body}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs text-accent">
                      Open form <ArrowRight className="h-3 w-3" />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl border border-border-subtle bg-bg-elevated p-6">
              <p className="eyebrow">Quick enquiry</p>
              <h2 className="mt-2 font-display text-xl text-text-primary">Send us a message</h2>
              <p className="mt-2 text-sm text-text-secondary">
                {"Anything not covered by the specialised forms — we'll route it from here."}
              </p>
              <div className="mt-4">
                <InquiryForm formType="contact_general" />
              </div>
            </div>
          </aside>
        </div>
      </Container>
    </>
  );
}
